(ns frontend.view.nev.surveys.form.components
    (:require
     [common.localization :as loc]
     [common.util :as cu]
     [frontend.utils :as fu]
     [frontend.view.nev.surveys.form.events :as events]
     [oops.core :as oops]
     [re-frame.core :as rf]
     [reagent.core :as r]
     [ui.components.button.view :as button]
     [ui.components.radix-ui.tooltip.view :as tooltip]
     [ui.components.rich-text.tiptap :as tiptap]
     [ui.components.select.select :as select]
     [ui.components.toggle.view :as toggle]
     [ui.dom-globals :as dg]
     [ui.editors.data-input :as data-input]
     [ui.util :as uu]))

(defn editor-exit-button
  []
  [button/secondary-open
   {:on-click (fn [e]
                (dg/prevent-and-stop e)
                (rf/dispatch [::events/exit-editor]))}
   (loc/tr :exit)])

(defn editor-exit-without-saving-button
  []
  [button/secondary-open
   {:on-click (fn [e]
                (dg/prevent-and-stop e)
                (rf/dispatch [::events/exit-editor]))}
   (loc/tr :exit-without-saving)])

(defn editor-save-button
  [disabled? dispatch-on-click]
  [button/primary-solid
   {:on-click  (fn [e]
                 (dg/prevent-and-stop e)
                 (rf/dispatch dispatch-on-click))
    :disabled? disabled?}
   (loc/tr :save)])

(defn editor-delete-button
  [disabled?]
  [button/danger-solid
   {:on-click  (fn [e]
                 (dg/prevent-and-stop e)
                 (rf/dispatch [::events/delete-process]))
    :disabled? disabled?}
   (loc/tr :delete)])

(defn editor-next-button
  [disabled? dispatch-on-click]
  [button/primary-solid
   {:on-click  (fn [e]
                 (dg/prevent-and-stop e)
                 (rf/dispatch dispatch-on-click))
    :disabled? disabled?}
   (loc/tr :submit)])

(defn missing-fields-button
  [missing-fields]
  [tooltip/view
   [:div
    {:class "bg-disabled-light text-disabled-light-text"}
    (loc/tr :missing-fields)]
   [:div
    [:h2
     {:class "text-xl font-semibold"}
     (loc/tr :fill-in-mandatory-fields)]
    [:ul
     (->> missing-fields
          (map (fn [lbl]
                 ^{:key (str "missing-mandatory-field-" lbl)}
                 [:li lbl]))
          (take 5))
     (when (> (count missing-fields) 5)
       [:li (loc/tr :and-other-fields [(- (count missing-fields) 5)])])]]])

(defn survey-card
  [content]
  [:div
   {:class "p-2 border border-solid border-base-light-border-paler rounded"}
   (into
     [:div
      {:class "flex flex-col gap-3"}]
     [content])])

(defn question-view
  [{:keys [survey-question-id label disabled? mandatory? precision data-unit data-type options value
           previous-value calculated? read-only?]}]
  [:<>

   ;[:span (pr-str question-config)]
   [:div
    {:class "flex flex-1"}
    [:label
     {:class "flex flex-1"}
     (when mandatory?
       [:span
        {:class "m-0 pe-2 text-danger-light-text font-sm"}
        "*"])
     label]
    [:div
     (when data-unit
       [:span
        {:class "mb-0 ps-2 font-sm text-sem"}
        data-unit])]]
   (when previous-value
     (let [;; These data types return hiccup/component from formatted-display-string
           renders-as-component? (contains? #{:data.type/text-multiline
                                              :data.type/text-richtext}
                                            data-type)]
       [:div
        {:class "-mt-2 pe-2 text-complement-light-text text-xs"}
        [:span (loc/tr :previous-answer)]
        (if renders-as-component?
          [:div {:class "inline"} previous-value]
          [:span (str previous-value)])]))
   (if calculated?
     [:<>
      [:div
       {:class "p-2 border border-solid border-base-light-border-paler text-base-regular-text bg-base-regular rounded"}
       [:i.fal.fa-calculator]
       " "
       (fu/round value (or precision 0))]]
     [:<>
      [data-input/view
       {:data-type              data-type
        :value                  (if disabled? nil value)
        :date-format            "dd.MM.yyyy"
        :disabled?              (or read-only? disabled?)
        :max-decimals           precision
        :options                options
        :on-valid-status-change #(rf/dispatch [::events/on-answer-validity-change survey-question-id %])
        :on-focus               #(rf/dispatch [::events/on-focus-question survey-question-id])
        :on-blur                #(rf/dispatch [::events/on-blur-question survey-question-id])
        :on-change              #(rf/dispatch [::events/on-answer-input survey-question-id %])}]])])

(defn questions-hiccup
  [{:keys [lang label content read-only?]}]
  (->> content
       (map-indexed
         (fn [item-index {:keys [hidden? cannot-skip? calculated? mandatory? value no-answer? survey-question-id survey-item-type] :as content-item}]
           (when-not hidden?
            ;; safe to use item-index here as the collection is static - the order never changes while viewing
             ^{:key (str "section-" label "-" item-index)}
             [:li
              {:class (uu/cm "gap-2 block"
                             (when hidden? "hidden"))}
              [:div
               {:class "flex flex-col gap-3"}
               [:div
                {:class "flex flex-1"}
                [:label
                 {:class "-mb-1 flex flex-1 text-xs justify-center self-end"}
                 (cond
                   (or calculated?
                       (not= :survey-item/question survey-item-type)
                       cannot-skip?
                       (not mandatory?))
                   ""
                   no-answer?
                   (loc/tr :toggle-to-not-answer-survey-question)
                   (cu/empty-value? value)
                   (loc/tr :toggle-to-answer-enabled-empty-value)
                   :else
                   (loc/tr :toggle-to-answer-enabled-with-value))]]
               [:div
                {:class "w-14 h-11 flex justify-center"}
                (when (and (= :survey-item/question survey-item-type)
                           (not (:calculated? content-item))
                           (not cannot-skip?)
                           mandatory?)
                  [toggle/toggle
                   {:checked?  (not no-answer?)
                    :on-change #(rf/dispatch [::events/on-answer-no-response survey-question-id (not no-answer?)])}])]]
              [:div
               {:class "flex flex-col gap-2 flex-1"}
               (case survey-item-type
                 :survey-item/wysiwyg
                 [tiptap/viewer
                  {:value (:rich-content content-item)
                   :styles {:border "none"
                            :backgroundColor "transparent"}}]
                 :survey-item/question
                 [question-view
                  (assoc content-item
                         :lang lang
                         :disabled? no-answer?
                         :read-only? read-only?)]
                 (str "Unknown survey-item-type " survey-item-type))]])))
       (remove nil?)
       (doall)))

(defn observe
  [& args]
  (js/console.log "Observe " args))

(defn survey-section
  [{:keys [status-strip-height] :as _block}]
  (let [state (r/atom {})
        scroll-listener (fn [_e]
                          (let [dom-node (:dom-node @state)
                                rect (oops/ocall dom-node "getBoundingClientRect")
                                top (aget rect "top")
                                height (aget rect "height")]
                            (if (and (< top
                                        (if (number? status-strip-height)
                                          status-strip-height
                                          0))
                                     (< (- top) height))
                              (let [info-node (:info-dom @state)
                                    info-rect (oops/ocall info-node "getBoundingClientRect")
                                    info-height (aget info-rect "height")
                                    movable-height (max 0 (- height info-height))
                                    new-top (+ (or status-strip-height 0)
                                               (min movable-height (- top)))]
                                (swap! state assoc :info-top new-top))
                              (when (not= 0 (:info-top @state))
                                (swap! state assoc :info-top 0)))))]
    (r/create-class
      {:component-did-mount
       (fn [_]
         (.addEventListener js/document "scroll" scroll-listener (clj->js {:capture true})))
       :component-will-unmount
       (fn [_]
         (.removeEventListener js/document "scroll" scroll-listener (clj->js {:capture true})))
       :reagent-render
       (fn [{:keys [label block-info] :as block}]
         (let [questions-hiccup (questions-hiccup block)]
           (when (seq questions-hiccup)
             [:div {:class "p-4 rounded bg-base-gloomy"}
              [:h2 {:class "font-semibold"} label]
              [:div {:class "p-4"}
               [:div {:class "w-full py-4 flex"}
                [:ul {:class "py-3 px-4 flex flex-col gap-3 flex-1"}
                 questions-hiccup]
                [:div
                 {:class "p-4 bg-base-gloomy rounded"
                  :ref   (fn [r]
                           (when-not (:dom-node @state)
                             (swap! state assoc :dom-node r)))}
                 [:div
                  {:class (uu/cm (when (:info-top @state) "relative top-4 transition"))
                   :ref   (fn [r]
                            (when-not (:info-dom @state)
                              (swap! state assoc :info-dom r)))}
                  [tiptap/viewer
                   {:value (if (cu/blank-value? block-info)
                             ""
                             block-info)
                    :styles {:border "none"
                             :backgroundColor "transparent"}}]]]]]])))})))

(defn wysiwyg-section
  [block]
  (let [wysiwyg-text (:wysiwyg-text block)]
    (when-not (cu/blank-value? wysiwyg-text)
      [:div
       [:div
        [tiptap/viewer
         {:value wysiwyg-text
          :styles {:border "none"
                   :backgroundColor "transparent"}}]]])))

(defn survey-header
  [{:keys [lang lang-opts on-lang-change logo survey-label]}]
  [:div
   [:div
    [:div {:class "w-full flex justify-between flex-1"}
     [:div
      {:class "w-full h-full flex flex-col items-center flex-1 gap-3"}
      [:img
       {:src   logo
        :class "max-w-100 min-w-48 max-h-36 min-h-12"}]]
     [:div
      {:class "flex justify-end self-center flex-[1_1_220%]"}
      [:span survey-label]]

     (when (and (seq lang-opts)
                (> (count lang-opts) 1))
       [:div
        {:class "flex-1"}
        [select/view
         {:options   lang-opts
          :value     lang
          :on-change #(rf/dispatch (vec (concat on-lang-change [%])))}]])]]])
